import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, ProgressBar, Row, Spinner, Table } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { PROJECT_STATUS, UPLOAD_BASE, fmtBaht, fmtDateTH, fmtDateTimeTH } from './aoShared'

// รายละเอียดโครงการ — ธุรการจัดการเต็ม / เจ้าของโครงการรายงานผลได้
export default function ProjectDetail() {
  const { pjId } = useParams()
  const navigate = useNavigate()
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showUpdate, setShowUpdate] = useState(false)
  const [upForm, setUpForm] = useState({ pu_note: '', pu_progress: '', pu_spent: '' })
  const [upFile, setUpFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/ao/projects/${pjId}`)
      if (res.data.success) setProject(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [pjId])

  useEffect(() => { fetchData() }, [fetchData])

  const openUpdate = () => {
    const lastProgress = project?.updates?.[0]?.pu_progress || 0
    setUpForm({ pu_note: '', pu_progress: String(lastProgress), pu_spent: '' })
    setUpFile(null)
    setModalError('')
    setShowUpdate(true)
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    setSaving(true)
    setModalError('')
    try {
      const fd = new FormData()
      Object.entries(upForm).forEach(([k, v]) => fd.append(k, v ?? ''))
      if (upFile) fd.append('file', upFile)
      await axiosInstance.post(`/api/ao/projects/${pjId}/updates`, fd)
      setShowUpdate(false)
      fetchData()
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const setStatus = async (status, label) => {
    const result = await Swal.fire({
      icon: 'question', title: `${label}?`, text: project.pj_name,
      showCancelButton: true, confirmButtonText: 'ยืนยัน', cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/projects/${pjId}`, {
        pj_year: project.pj_year,
        pj_name: project.pj_name,
        pj_department: project.pj_department || '',
        pj_owner_t_id: project.pj_owner_t_id || '',
        pj_objective: project.pj_objective || '',
        pj_budget: project.pj_budget,
        pj_start: project.pj_start || '',
        pj_end: project.pj_end || '',
        pj_status: status,
      })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ดำเนินการไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const closeWithEval = async () => {
    const { value: formValues } = await Swal.fire({
      title: 'ปิดโครงการ + ประเมินผล',
      html: `
        <select id="sw-score" class="form-select form-select-sm mb-2">
          <option value="">— คะแนนประเมิน —</option>
          ${[5, 4, 3, 2, 1].map(s => `<option value="${s}">${s} คะแนน</option>`).join('')}
        </select>
        <textarea id="sw-result" class="form-control form-control-sm" rows="3"
          placeholder="สรุปผลการดำเนินงาน"></textarea>`,
      showCancelButton: true,
      confirmButtonText: 'ปิดโครงการ',
      cancelButtonText: 'ปิด',
      preConfirm: () => ({
        pj_eval_score: document.getElementById('sw-score').value,
        pj_result: document.getElementById('sw-result').value,
      }),
    })
    if (!formValues) return
    try {
      await axiosInstance.put(`/api/ao/projects/${pjId}`, {
        pj_year: project.pj_year,
        pj_name: project.pj_name,
        pj_department: project.pj_department || '',
        pj_owner_t_id: project.pj_owner_t_id || '',
        pj_objective: project.pj_objective || '',
        pj_budget: project.pj_budget,
        pj_start: project.pj_start || '',
        pj_end: project.pj_end || '',
        pj_status: 'done',
        ...formValues,
      })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ปิดโครงการไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const st = project ? (PROJECT_STATUS[project.pj_status] || PROJECT_STATUS.planned) : null
  const budget = Number(project?.pj_budget || 0)
  const spent = Number(project?.pj_spent || 0)
  const lastProgress = project?.updates?.[0]?.pu_progress ?? 0

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              {/* หน้านี้ใช้ร่วมระหว่างธุรการและเจ้าของโครงการ — ย้อนกลับตามเส้นทางที่มา */}
              <span role="button" className="me-2" title="ย้อนกลับ" onClick={() => navigate(-1)}>
                <i className="ri-arrow-left-line" />
              </span>
              <i className="ri-bar-chart-box-line me-2 text-primary" />
              รายละเอียดโครงการ
            </h4>
            {project && (
              <div className="d-flex gap-2 flex-wrap">
                <Button variant="primary" size="sm" onClick={openUpdate}>
                  <i className="ri-add-line me-1" />รายงานความคืบหน้า
                </Button>
                {project.pj_status === 'planned' && (
                  <Button variant="outline-success" size="sm" onClick={() => setStatus('approved', 'อนุมัติแผน')}>อนุมัติแผน</Button>
                )}
                {['approved', 'in_progress'].includes(project.pj_status) && (
                  <Button variant="success" size="sm" onClick={closeWithEval}>
                    <i className="ri-check-double-line me-1" />ปิดโครงการ+ประเมิน
                  </Button>
                )}
              </div>
            )}
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : !project ? (
            <Alert variant="warning">ไม่พบโครงการ</Alert>
          ) : (
            <Row className="g-3">
              <Col lg={5}>
                <Card className="card-one">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <Card.Title as="h6" className="mb-0">ข้อมูลโครงการ</Card.Title>
                    <Badge bg={st.bg}>{st.label}</Badge>
                  </Card.Header>
                  <Card.Body className="fs-sm">
                    <div className="fw-semibold mb-1">{project.pj_name}</div>
                    <Table borderless size="sm" className="mb-0">
                      <tbody>
                        <tr><td className="text-muted" style={{ width: 120 }}>ปี</td><td>พ.ศ. {project.pj_year}</td></tr>
                        <tr><td className="text-muted">ฝ่าย</td><td>{project.pj_department || '-'}</td></tr>
                        <tr><td className="text-muted">ผู้รับผิดชอบ</td><td>{project.owner_name || '-'}</td></tr>
                        <tr><td className="text-muted">ระยะเวลา</td><td>{fmtDateTH(project.pj_start)} - {fmtDateTH(project.pj_end)}</td></tr>
                        {project.pj_objective && <tr><td className="text-muted">วัตถุประสงค์</td><td style={{ whiteSpace: 'pre-wrap' }}>{project.pj_objective}</td></tr>}
                        <tr>
                          <td className="text-muted">เล่มโครงการ</td>
                          <td>{project.pj_file
                            ? <a href={`${UPLOAD_BASE}/uploads/ao_docs/${project.pj_file}`} target="_blank" rel="noreferrer"><i className="ri-attachment-2" /> เปิดไฟล์</a>
                            : '-'}</td>
                        </tr>
                      </tbody>
                    </Table>
                    <hr />
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">งบประมาณ</span>
                      <span className="fw-semibold">{fmtBaht(budget)} บาท</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">เบิกจ่ายสะสม</span>
                      <span className={spent > budget ? 'text-danger fw-semibold' : 'fw-semibold'}>{fmtBaht(spent)} บาท</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">คงเหลือ</span>
                      <span>{fmtBaht(budget - spent)} บาท</span>
                    </div>
                    <div className="text-muted mb-1">ความคืบหน้าล่าสุด {lastProgress}%</div>
                    <ProgressBar now={lastProgress} variant="info" style={{ height: 10 }} />
                    {project.pj_status === 'done' && (
                      <Alert variant="light" className="border fs-sm py-2 mt-3 mb-0">
                        <div><span className="text-muted">คะแนนประเมิน:</span> {project.pj_eval_score ? `${project.pj_eval_score}/5` : '-'}</div>
                        {project.pj_result && <div><span className="text-muted">สรุปผล:</span> {project.pj_result}</div>}
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={7}>
                <Card className="card-one">
                  <Card.Header><Card.Title as="h6" className="mb-0">รายงานความคืบหน้า ({project.updates.length})</Card.Title></Card.Header>
                  <Card.Body className="p-0">
                    {project.updates.length === 0 ? (
                      <div className="text-center text-muted py-4">ยังไม่มีรายงานความคืบหน้า</div>
                    ) : (
                      <Table responsive hover className="mb-0 fs-sm">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 150 }}>วันที่</th>
                            <th>รายละเอียด</th>
                            <th className="text-center" style={{ width: 100 }}>คืบหน้า</th>
                            <th className="text-end" style={{ width: 120 }}>เบิกจ่าย</th>
                            <th style={{ width: 50 }}>ไฟล์</th>
                          </tr>
                        </thead>
                        <tbody>
                          {project.updates.map(u => (
                            <tr key={u.pu_id}>
                              <td>
                                {fmtDateTimeTH(u.created_at)}
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>{u.created_by_name}</div>
                              </td>
                              <td style={{ whiteSpace: 'pre-wrap' }}>{u.pu_note}</td>
                              <td className="text-center">{u.pu_progress}%</td>
                              <td className="text-end">{u.pu_spent ? fmtBaht(u.pu_spent) : '-'}</td>
                              <td>
                                {u.pu_file ? (
                                  <a href={`${UPLOAD_BASE}/uploads/ao_docs/${u.pu_file}`} target="_blank" rel="noreferrer">
                                    <i className="ri-attachment-2" />
                                  </a>
                                ) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      <Footer />

      {/* Progress-update modal */}
      <Modal show={showUpdate} onHide={() => setShowUpdate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">รายงานความคืบหน้า</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdate}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">รายละเอียด <span className="text-danger">*</span></Form.Label>
              <Form.Control size="sm" as="textarea" rows={3} required value={upForm.pu_note}
                onChange={e => setUpForm(f => ({ ...f, pu_note: e.target.value }))} />
            </Form.Group>
            <Row className="g-2 mb-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">ความคืบหน้า (%) <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="number" min="0" max="100" required value={upForm.pu_progress}
                  onChange={e => setUpForm(f => ({ ...f, pu_progress: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">เบิกจ่ายงวดนี้ (บาท)</Form.Label>
                <Form.Control size="sm" type="number" min="0" step="0.01" value={upForm.pu_spent}
                  onChange={e => setUpForm(f => ({ ...f, pu_spent: e.target.value }))} />
              </Col>
            </Row>
            <Form.Group>
              <Form.Label className="fs-sm mb-1">ไฟล์แนบ <span className="text-muted">— ไม่บังคับ</span></Form.Label>
              <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={e => setUpFile(e.target.files[0] || null)} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowUpdate(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}
              บันทึกรายงาน
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
